import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { LogIn } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkSetup();
  }, []);

  const checkSetup = async () => {
    try {
      const response = await axios.get(`${API}/auth/check-setup`);
      setNeedsSetup(response.data.needs_setup);
    } catch (error) {
      console.error('Error checking setup:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/setup-first-admin`, {
        username,
        email,
        password,
        full_name: fullName
      });

      toast.success('Admin account created! Please log in.');
      setNeedsSetup(false);
      setEmail('');
      setFullName('');
    } catch (error) {
      console.error('Setup error:', error);
      toast.error(error.response?.data?.detail || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl border-t-4 border-cme-red">
        <div className="flex flex-col items-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_cme-join-modal/artifacts/wocpjpx8_CME2020-HORIZONTAL.jpg" 
            alt="CME Logo"
            className="h-16 w-auto mb-4"
          />
          <h1 className="text-3xl font-serif font-bold text-black">
            {needsSetup ? 'Admin Setup' : 'Admin Login'}
          </h1>
          <p className="text-gray-600 text-sm mt-2">
            {needsSetup ? 'Create your first admin account' : 'Sign in to access the dashboard'}
          </p>
        </div>

        <form onSubmit={needsSetup ? handleSetup : handleLogin} className="space-y-4">
          {needsSetup && (
            <>
              <div>
                <Label htmlFor="fullName" className="text-sm font-semibold text-gray-700">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="mt-1 h-11"
                  placeholder="Kiki Smith"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 h-11"
                  placeholder="kiki@cmegraphics.com"
                />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="username" className="text-sm font-semibold text-gray-700">
              Username
            </Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="mt-1 h-11"
              placeholder="admin"
              autoComplete="username"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 h-11"
              placeholder="••••••••"
              autoComplete={needsSetup ? "new-password" : "current-password"}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-cme-red hover:bg-red-700 text-white font-semibold text-base mt-6"
          >
            {loading ? (
              'Please wait...'
            ) : (
              <>
                <LogIn className="w-5 h-5 mr-2" />
                {needsSetup ? 'Create Admin Account' : 'Sign In'}
              </>
            )}
          </Button>
        </form>

        {!needsSetup && (
          <p className="text-center text-sm text-gray-500 mt-6">
            Forgot password? Contact system administrator.
          </p>
        )}
      </Card>
    </div>
  );
};

export default AdminLogin;
